import { useState, useEffect } from 'react'
import { useGameStore } from '../store/gameStore'

interface LearningPoint {
  id: string
  title: string
  description: string
  icon: string
  quiz?: {
    question: string
    options: string[]
    correctAnswer: number
  }
}

// 学習カテゴリーマッピング
const CATEGORY_MAP: Record<string, string> = {
  '水循環の仕組み': 'water',
  '降雨と植物の関係': 'water',
  '海洋の形成': 'water',
  '海洋生態系': 'water',
  '火山活動のメカニズム': 'geology',
  'プレートテクトニクス': 'geology',
  '地震と津波の関係': 'geology',
  'CO2と温室効果': 'climate',
  '大気の組成': 'climate',
  '光合成の仕組み': 'life',
  '生態系のバランス': 'life',
  '文明の発展': 'life',
  '資源の形成': 'geology'
}

// 学習内容の詳細データ
const LEARNING_DATA: Record<string, LearningPoint[]> = {
  '水循環の仕組み': [{
    id: 'water-1',
    title: '💧 水のぼうけん',
    description: '水は空にのぼって雲になり、雨になって地面にもどってくるよ！これを「水じゅんかん」っていうんだ。',
    icon: '☁️',
    quiz: {
      question: '⚠️ きけん！地球があぶない！\n雲はなにでできている？',
      options: ['あわ', '水のつぶ', 'けむり', 'わたがし'],
      correctAnswer: 1
    }
  }],
  '降雨と植物の関係': [{
    id: 'rain-plant-1',
    title: '🌱 植物と雨のなかよし',
    description: '植物は根っこから水をすいあげて大きくなるよ！雨がふると植物がよろこぶんだ。',
    icon: '🌿'
  }],
  '火山活動のメカニズム': [{
    id: 'volcano-1',
    title: '🌋 火山のひみつ',
    description: '地球の中はとってもあつくて、ドロドロの「マグマ」があるよ。それが外に出てくるのが火山なんだ！',
    icon: '🔥',
    quiz: {
      question: '🌋 地球があつくなりすぎてる！\n火山から出てくるドロドロしたものを何という？',
      options: ['みず', 'マグマ', 'どろ', 'ゼリー'],
      correctAnswer: 1
    }
  }],
  'CO2と温室効果': [{
    id: 'co2-1',
    title: '🏠 地球のあったかハウス',
    description: 'CO2（二酸化炭素）は地球をあたたかくするガスだよ。でも多すぎると地球があつくなりすぎちゃう！',
    icon: '🌡️'
  }],
  'プレートテクトニクス': [{
    id: 'plate-1',
    title: '🧩 地球のパズル',
    description: '地球の表面は大きなパズルのピースみたいに分かれているよ。これが動くと地震がおきるんだ！',
    icon: '🗾',
    quiz: {
      question: '🏚️ たいへん！地震がおきそう！\n地震はなぜおきる？',
      options: ['かみなりのせい', '地面のパズルが動くから', 'おおきな音のせい', 'きょうりゅうが歩くから'],
      correctAnswer: 1
    }
  }],
  '地震と津波の関係': [{
    id: 'tsunami-1',
    title: '🌊 大きな波のはなし',
    description: '海の下で地震がおきると、大きな波「つなみ」ができることがあるよ。とてもきけんだから高いところににげよう！',
    icon: '🏃'
  }],
  '海洋の形成': [{
    id: 'ocean-1',
    title: '🌊 海のたんじょう',
    description: 'むかしむかし、地球がひえて雨がたくさんふって、その水がたまって海ができたんだよ！',
    icon: '🐠'
  }],
  '海洋生態系': [{
    id: 'ocean-eco-1',
    title: '🐟 海のいきものたち',
    description: '海にはたくさんの生きものがすんでいて、みんなたすけあって生きているよ！',
    icon: '🦈'
  }],
  '光合成の仕組み': [{
    id: 'photosynthesis-1',
    title: '☀️ 植物のごはんづくり',
    description: '植物は太陽の光をあびて、自分でごはん（えいよう）をつくれるすごい力があるよ！',
    icon: '🌻',
    quiz: {
      question: '🥀 植物がかれちゃう！\n植物がごはんをつくるのに必要なものは？',
      options: ['太陽の光', 'でんき', 'ガソリン', 'さとう'],
      correctAnswer: 0
    }
  }],
  '生態系のバランス': [{
    id: 'ecosystem-1',
    title: '⚖️ みんなでバランス',
    description: '動物も植物もむしも、みんながいるから地球はげんきなんだ。だれかがいなくなるとこまっちゃう！',
    icon: '🦋'
  }],
  '文明の発展': [{
    id: 'civilization-1',
    title: '🏛️ 人間のれきし',
    description: '人間はどうぐをつくったり、のうぎょうをはじめたりして、どんどんかしこくなっていったよ！',
    icon: '👥'
  }],
  '大気の組成': [{
    id: 'atmosphere-1',
    title: '💨 空気のレシピ',
    description: '空気はさんそ（いきをするのに必要）とちっそなどがまざってできているよ！',
    icon: '🌬️'
  }],
  '資源の形成': [{
    id: 'resource-1',
    title: '💎 たからものができるまで',
    description: 'ながーい時間をかけて、植物や動物が石油や石炭にかわるんだ。だから大切につかおう！',
    icon: '⛏️'
  }]
}

interface LearningModalProps {
  isOpen: boolean
  onClose: () => void
  learningPoints: string[]
  actionName: string
}

export function LearningModal({ isOpen, onClose, learningPoints, actionName }: LearningModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showQuiz, setShowQuiz] = useState(false)
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null)
  const [showResult, setShowResult] = useState(false)
  const [earnedBadges, setEarnedBadges] = useState<string[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [earthImpact, setEarthImpact] = useState<'positive' | 'negative' | null>(null)
  const worldId = useGameStore(state => state.worldId)
  const loadWorld = useGameStore(state => state.loadWorld)
  
  if (!isOpen) return null
  
  const currentLearning = learningPoints[currentIndex]
  const learningData = LEARNING_DATA[currentLearning]?.[0]
  const category = CATEGORY_MAP[currentLearning] || 'general'
  
  if (!learningData) return null
  
  const handleNext = () => {
    if (currentIndex < learningPoints.length - 1) {
      setCurrentIndex(currentIndex + 1)
      setShowQuiz(false)
      setSelectedAnswer(null)
      setShowResult(false)
      setEarthImpact(null)
    } else {
      // バッジを獲得
      const newBadge = `${actionName}マスター`
      setEarnedBadges([...earnedBadges, newBadge])
      setTimeout(() => {
        onClose()
        setCurrentIndex(0)
      }, 2000)
    }
  }
  
  const handleQuizAnswer = async (answerIndex: number) => {
    if (isProcessing) return
    
    setSelectedAnswer(answerIndex)
    setShowResult(true)
    setIsProcessing(true)
    
    const isCorrect = answerIndex === learningData.quiz?.correctAnswer
    
    // APIに結果を送信
    if (worldId) {
      try {
        const response = await fetch(`http://localhost:8789/api/worlds/${worldId}/quiz-result`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isCorrect, category })
        })
        
        const data = await response.json()
        setEarthImpact(data.impact)
        
        // 地球の状態を更新
        await loadWorld(worldId)
      } catch (error) {
        console.error('Failed to submit quiz result:', error)
      }
    }
    
    setIsProcessing(false)
    
    if (isCorrect) {
      // 正解！
      setTimeout(() => {
        handleNext()
      }, 3000)
    }
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-bounce-in">
        {/* ヘッダー */}
        <div className="bg-gradient-to-r from-blue-400 to-green-400 p-6 rounded-t-3xl">
          <h2 className="text-3xl font-bold text-white flex items-center gap-3">
            <span className="text-5xl">🎓</span>
            まなびタイム！
          </h2>
          <p className="text-white mt-2">「{actionName}」でなにがおきたかな？</p>
        </div>
        
        {/* コンテンツ */}
        <div className="p-8">
          {!showQuiz ? (
            <>
              {/* 学習内容 */}
              <div className="text-center mb-6">
                <div className="text-8xl mb-4">{learningData.icon}</div>
                <h3 className="text-2xl font-bold mb-4 text-gray-800">
                  {learningData.title}
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed bg-yellow-50 p-6 rounded-2xl">
                  {learningData.description}
                </p>
              </div>
              
              {/* イラスト風の図解 */}
              <div className="bg-gradient-to-b from-sky-100 to-green-100 p-6 rounded-2xl mb-6">
                <div className="flex justify-center items-center gap-4">
                  {currentLearning.includes('水') && (
                    <div className="text-center">
                      <div className="text-4xl mb-2">☀️</div>
                      <div className="text-4xl animate-bounce">⬆️</div>
                      <div className="text-4xl">💧</div>
                      <div className="text-gray-600 text-sm">じょうはつ</div>
                    </div>
                  )}
                  {currentLearning.includes('火山') && (
                    <div className="text-center">
                      <div className="text-6xl animate-pulse">🌋</div>
                      <div className="text-gray-600 text-sm mt-2">マグマがふきだす！</div>
                    </div>
                  )}
                  {currentLearning.includes('植物') && (
                    <div className="text-center">
                      <div className="text-4xl">☀️</div>
                      <div className="text-4xl animate-pulse">🌱</div>
                      <div className="text-gray-600 text-sm">すくすく成長</div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* ボタン */}
              <div className="flex gap-4">
                {learningData.quiz && (
                  <button
                    onClick={() => setShowQuiz(true)}
                    className="flex-1 bg-orange-400 hover:bg-orange-500 text-white font-bold py-4 px-6 rounded-full text-lg transition-all transform hover:scale-105"
                  >
                    🎯 クイズにちょうせん！
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-6 rounded-full text-lg transition-all transform hover:scale-105"
                >
                  {currentIndex < learningPoints.length - 1 ? 'つぎへ →' : 'おわる ✨'}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* クイズ画面 */}
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">🤔</div>
                <h3 className="text-2xl font-bold mb-6 text-gray-800">
                  {learningData.quiz?.question}
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  {learningData.quiz?.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => !showResult && handleQuizAnswer(index)}
                      disabled={showResult}
                      className={`
                        p-6 rounded-2xl text-lg font-bold transition-all transform hover:scale-105
                        ${selectedAnswer === index
                          ? index === learningData.quiz?.correctAnswer
                            ? 'bg-green-400 text-white'
                            : 'bg-red-400 text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                        }
                        ${showResult && index === learningData.quiz?.correctAnswer
                          ? 'bg-green-400 text-white ring-4 ring-green-300'
                          : ''
                        }
                      `}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                
                {showResult && (
                  <div className={`mt-6 p-6 rounded-2xl ${
                    selectedAnswer === learningData.quiz?.correctAnswer 
                      ? 'bg-green-100' 
                      : 'bg-red-100'
                  }`}>
                    {selectedAnswer === learningData.quiz?.correctAnswer ? (
                      <div className="text-center">
                        <div className="text-6xl mb-2">🎉</div>
                        <p className="text-2xl font-bold text-green-600 mb-2">
                          せいかい！すごい！
                        </p>
                        <p className="text-lg text-green-700">
                          🌍 地球がかいふくしました！
                        </p>
                        <div className="mt-4 text-4xl animate-pulse">
                          💚➡️🌍✨
                        </div>
                      </div>
                    ) : (
                      <div className="text-center">
                        <div className="text-6xl mb-2 animate-bounce">💀</div>
                        <p className="text-2xl font-bold text-red-600 mb-2">
                          ざんねん...地球があぶない！
                        </p>
                        <p className="text-lg text-red-700">
                          ⚠️ 地球にダメージが！
                        </p>
                        <div className="mt-4 text-4xl">
                          🌍➡️💔😱
                        </div>
                      </div>
                    )}
                    
                    {earthImpact && (
                      <div className="mt-4 p-3 bg-white rounded-xl">
                        <p className="text-sm text-gray-700">
                          {earthImpact === 'positive' ? (
                            <>✅ 水がふえた！植物がそだった！空気がきれいになった！</>
                          ) : (
                            <>❌ 温度があがった！どくがふえた！生きものがへった！</>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {selectedAnswer !== learningData.quiz?.correctAnswer && showResult && (
                <button
                  onClick={() => {
                    setSelectedAnswer(null)
                    setShowResult(false)
                  }}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-6 rounded-full text-lg transition-all transform hover:scale-105"
                >
                  もういちど！
                </button>
              )}
            </>
          )}
          
          {/* 進捗表示 */}
          <div className="mt-6 flex justify-center gap-2">
            {learningPoints.map((_, index) => (
              <div
                key={index}
                className={`
                  w-3 h-3 rounded-full transition-all
                  ${index === currentIndex
                    ? 'bg-blue-500 w-8'
                    : index < currentIndex
                    ? 'bg-green-400'
                    : 'bg-gray-300'
                  }
                `}
              />
            ))}
          </div>
        </div>
        
        {/* バッジ獲得アニメーション */}
        {earnedBadges.length > 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-3xl">
            <div className="bg-white p-8 rounded-3xl text-center animate-bounce-in">
              <div className="text-8xl mb-4">🏆</div>
              <h3 className="text-3xl font-bold text-yellow-500 mb-2">
                バッジゲット！
              </h3>
              <p className="text-xl text-gray-700">{earnedBadges[earnedBadges.length - 1]}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}